/* Store Locator — Full JS
   Features:
   - Cascading State → City → Locality dropdowns
   - Address keyword search
   - Geolocation "Use My Current Location"
   - Distance calculation (Haversine)
   - Open Now toggle (simulated hours)
   - Custom red SVG map markers
   - Store card render with rating, status, actions
   - Map marker click ↔ card highlight sync
*/

(function () {
  'use strict';

  /* ── Simulated store hours (9AM–9PM every day) ── */
  function isOpenNow() {
    const h = new Date().getHours();
    return h >= 9 && h < 21;
  }

  /* ── Simulated rating (deterministic from store id) ── */
  function storeRating(id) {
    const ratings = [4.7, 4.5, 4.3, 4.1, 4.4, 4.6, 4.2, 4.8, 4.0, 4.5];
    return ratings[id % ratings.length];
  }

  function reviewCount(id) {
    const counts = [325, 210, 150, 89, 412, 278, 195, 543, 67, 320];
    return counts[id % counts.length];
  }

  /* ── Haversine distance (km) ── */
  function haversine(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /* ── Star string ── */
  function starHTML(rating) {
    const full  = Math.floor(rating);
    const half  = rating - full >= 0.5 ? 1 : 0;
    const empty = 5 - full - half;
    let s = '';
    for (let i = 0; i < full;  i++) s += '<span class="sl-star">★</span>';
    for (let i = 0; i < half;  i++) s += '<span class="sl-star" style="opacity:.5">★</span>';
    for (let i = 0; i < empty; i++) s += '<span style="color:#ddd">★</span>';
    return s;
  }

  /* ── Custom red marker SVG ── */
  function redMarkerIcon(active) {
    const colour = active ? '#8b0000' : '#c8102e';
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="30" height="42" viewBox="0 0 30 42">
        <path d="M15 0C6.7 0 0 6.7 0 15c0 10 15 27 15 27s15-17 15-27C30 6.7 23.3 0 15 0z" fill="${colour}"/>
        <circle cx="15" cy="15" r="7" fill="#fff" opacity=".9"/>
        <rect x="11" y="11" width="8" height="8" rx="1" fill="${colour}"/>
      </svg>`;
    return L.divIcon({
      className: 'sl-custom-marker',
      html: svg,
      iconSize:   [30, 42],
      iconAnchor: [15, 42],
      popupAnchor:[0, -44],
    });
  }

  /* ────────────────────────────────────────────
     MAIN INIT
  ─────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', async () => {
    if (!document.getElementById('store-map')) return;

    /* ── DOM refs ── */
    const mapEl       = document.getElementById('store-map');
    const storeList   = document.getElementById('store-list');
    const stateSelect = document.getElementById('sl-state');
    const citySelect  = document.getElementById('sl-city');
    const addrInput   = document.getElementById('sl-address-input');
    const searchBtn   = document.getElementById('sl-search-btn');
    const advSearchBtn= document.getElementById('sl-adv-search-btn');
    const locateBtn   = document.getElementById('sl-locate-btn');
    const useLocBtn   = document.getElementById('sl-use-location');
    const openNowChk  = document.getElementById('sl-open-now');
    const resultsBar  = document.getElementById('sl-results-bar');
    const countEl     = document.getElementById('sl-count');
    const paginationEl= document.getElementById('sl-pagination');

    /* ── Pagination state ── */
    const ITEMS_PER_PAGE = 10;
    let currentPage    = 1;
    let filteredStores = [];

    /* ── Tab switching ── */
    document.querySelectorAll('.sl-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        const target = tab.dataset.tab;
        // Toggle active class on tabs
        document.querySelectorAll('.sl-tab').forEach(t => t.classList.remove('sl-tab--active'));
        tab.classList.add('sl-tab--active');
        // Toggle panels
        document.getElementById('sl-panel-nearby').classList.toggle('sl-panel--hidden',   target !== 'nearby');
        document.getElementById('sl-panel-advanced').classList.toggle('sl-panel--hidden', target !== 'advanced');
        // Reset filters on switch
        if (target === 'nearby') {
          stateSelect.value = ''; citySelect.value = '';
        } else {
          addrInput.value = '';
        }
        applyFilters();
      });
    });

  /* ── Parse degree string (DMS) or decimal to number ── */
  function parseCoordinate(val) {
    if (val == null || val === '') return null;
    if (typeof val === 'number') return isNaN(val) ? null : val;

    const str = String(val).trim();
    if (!str) return null;

    // Check if simple decimal degree string (e.g. "30.32947036" or "-78.0466")
    if (/^-?\d+(\.\d+)?$/.test(str)) {
      const num = parseFloat(str);
      return isNaN(num) ? null : num;
    }

    // Parse DMS (Degrees, Minutes, Seconds) format e.g. "22 34;40'", "88 24'1''", "22° 34' 40\" N"
    const numbers = str.match(/[-+]?\d+(\.\d+)?/g);
    if (numbers && numbers.length > 0) {
      const deg = parseFloat(numbers[0]) || 0;
      const min = parseFloat(numbers[1]) || 0;
      const sec = parseFloat(numbers[2]) || 0;

      let decimal = Math.abs(deg) + (min / 60) + (sec / 3600);
      if (deg < 0 || /S|W/i.test(str)) {
        decimal = -decimal;
      }
      return decimal;
    }

    return null;
  }

  /* ── Normalize store object from old or new JSON formats ── */
  function normalizeStore(raw, index) {
    if (!raw || typeof raw !== 'object') return null;

    const id = raw.id ?? raw.ID ?? (index + 1);
    const name = raw.name ?? raw.Name ?? 'United Colors of Benetton';
    const city = raw.city ?? raw.City ?? '';
    const state = raw.state ?? raw.State ?? '';
    const phone = raw.phone ?? raw.Phone ?? '';
    const pincode = raw.pincode ?? raw.Pincode ?? '';
    const ncode = raw.ncode ?? raw.Ncode ?? '';
    const pincodeCityState = raw['Pincode City State'] ?? raw.pincodeCityState ?? '';

    // Handle single 'address'/'Address' or split 'Address 1'/'Address 2'
    const address1 = String(raw['Address 1'] ?? raw.address1 ?? raw.Address1 ?? raw.address ?? raw.Address ?? '').trim();
    const address2 = String(raw['Address 2'] ?? raw.address2 ?? raw.Address2 ?? '').trim();
    const fullAddr = [address1, address2].filter(Boolean).join(', ');

    // Handle key variations including typos like "Logitude (degree)"
    const rawLat = raw.latitude ?? raw.Latitude ?? raw['Latitude (degree)'] ?? raw['Latitude(degree)'] ?? raw.lat;
    const rawLng = raw.longitude ?? raw.Longitude ?? raw['Logitude (degree)'] ?? raw['Longitude (degree)'] ?? raw['Logitude(degree)'] ?? raw.lng ?? raw.lon;

    const lat = parseCoordinate(rawLat);
    const lng = parseCoordinate(rawLng);

    return {
      id: id,
      name: String(name).trim(),
      city: String(city).trim(),
      state: String(state).trim(),
      address: fullAddr,
      address1: address1,
      address2: address2,
      phone: String(phone).trim(),
      pincode: String(pincode).trim(),
      ncode: String(ncode).trim(),
      pincodeCityState: String(pincodeCityState).trim(),
      latitude: lat,
      longitude: lng
    };
  }

  /* ── Load stores ── */
  let allStores = [];
  try {
    const res = await fetch(window.STORE_JSON_URL);
    if (!res.ok) throw new Error('Failed');
    const rawStores = await res.json();
    allStores = (Array.isArray(rawStores) ? rawStores : [])
      .map(normalizeStore)
      .filter(Boolean);
  } catch (e) {
    storeList.innerHTML = '<p style="padding:16px;color:#c8102e;font-size:13px;">Could not load store data.</p>';
    return;
  }

    /* ── Init map ── */
    const map = L.map('store-map', {
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,   // page scrolls normally; zoom via pinch / double-click / buttons
    }).setView([23.2, 78.5], 6);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(map);
    L.control.zoom({ position: 'bottomright' }).addTo(map);

    /* ── Marker registry ── */
    const markerMap = {}; // id → L.marker
    let userMarker  = null;
    let userLatLng  = null;

    /* ── Populate State dropdown ── */
    const states = [...new Set(allStores.map(s => s.state).filter(Boolean))].sort();
    states.forEach(st => {
      const opt = document.createElement('option');
      opt.value = st;
      opt.textContent = st;
      stateSelect.appendChild(opt);
    });

    /* ── Initial render: show all stores with pagination on load ── */
    applyFilters();

    /* ── State → City cascade + live filter ── */
    stateSelect.addEventListener('change', () => {
      const sel = stateSelect.value;
      citySelect.innerHTML = '<option value="">All</option>';
      if (sel) {
        [...new Set(allStores.filter(s => s.state === sel).map(s => s.city))].sort()
          .forEach(c => { const o = document.createElement('option'); o.value = o.textContent = c; citySelect.appendChild(o); });
      }
      applyFilters();
    });

    /* ── City change → live filter ── */
    citySelect.addEventListener('change', applyFilters);

    /* Open Now toggle → live filter */
    openNowChk.addEventListener('change', applyFilters);

    /* ── Geolocation shared handler ── */
    function doGeolocate(onSuccess) {
      if (!navigator.geolocation) {
        alert('Geolocation is not supported by your browser.');
        return;
      }
      addrInput.value = 'Detecting your location…';
      addrInput.disabled = true;
      navigator.geolocation.getCurrentPosition(
        pos => {
          userLatLng = { lat: pos.coords.latitude, lng: pos.coords.longitude };

          /* Place blue dot for user */
          if (userMarker) userMarker.remove();
          userMarker = L.circleMarker([userLatLng.lat, userLatLng.lng], {
            radius: 9, fillColor: '#1a73e8', color: '#fff',
            weight: 3, fillOpacity: 1
          }).addTo(map).bindPopup('You are here');

          /* Fly map to user */
          map.flyTo([userLatLng.lat, userLatLng.lng], 12, { duration: 1 });

          /* Update input text */
          addrInput.value = 'Current Location';
          addrInput.disabled = false;

          /* Re-render all stores sorted by distance */
          applyFilters();

          if (onSuccess) onSuccess();
        },
        err => {
          addrInput.value = '';
          addrInput.disabled = false;
          alert('Unable to retrieve your location. Please allow location access and try again.');
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }

    /* Wire up geolocation triggers (locateBtn may be absent) */
    if (locateBtn) locateBtn.addEventListener('click', () => doGeolocate());
    useLocBtn.addEventListener('click', () => doGeolocate());

    /* ── Live search: filter on every keystroke (Nearby panel) ── */
    addrInput.addEventListener('input', () => {
      if (addrInput.value !== 'Current Location') userLatLng = null;
      applyFilters();
    });
    addrInput.addEventListener('keydown', e => { if (e.key === 'Enter') applyFilters(); });
    searchBtn.addEventListener('click', applyFilters);

    advSearchBtn.addEventListener('click', applyFilters);

    /* ── Core filter & render ── */
    function applyFilters() {
      const keyword  = addrInput.value.toLowerCase().trim();
      const selState = stateSelect.value;
      const selCity  = citySelect.value;
      const openOnly = openNowChk.checked;

      let filtered = allStores.filter(s => {
        const matchKw = !keyword || keyword === 'current location' ||
          (s.name    && s.name.toLowerCase().includes(keyword)) ||
          (s.city    && s.city.toLowerCase().includes(keyword)) ||
          (s.state   && s.state.toLowerCase().includes(keyword)) ||
          (s.address && s.address.toLowerCase().includes(keyword)) ||
          (s.pincode && s.pincode.toLowerCase().includes(keyword)) ||
          (s.ncode   && s.ncode.toLowerCase().includes(keyword));
        const matchState = !selState || s.state === selState;
        const matchCity  = !selCity  || s.city  === selCity;
        const matchOpen  = !openOnly || isOpenNow();
        return matchKw && matchState && matchCity && matchOpen;
      });

      /* Sort by distance if geolocation was used */
      if (userLatLng) {
        filtered = filtered.map(s => ({
          ...s,
          _dist: (s.latitude != null && s.longitude != null && !isNaN(s.latitude) && !isNaN(s.longitude))
            ? haversine(userLatLng.lat, userLatLng.lng, s.latitude, s.longitude)
            : null
        })).sort((a, b) => {
          if (a._dist == null) return 1;
          if (b._dist == null) return -1;
          return a._dist - b._dist;
        });
      }

      filteredStores = filtered; // cache for pagination
      showResultsBar(filtered.length);
      renderPage(1);             // always start at page 1 on new search
    }

    /* ── Render a specific page ── */
    function renderPage(page) {
      const totalPages = Math.ceil(filteredStores.length / ITEMS_PER_PAGE);
      currentPage = Math.max(1, Math.min(page, totalPages || 1));

      const start = (currentPage - 1) * ITEMS_PER_PAGE;
      const pageStores = filteredStores.slice(start, start + ITEMS_PER_PAGE);

      renderCards(pageStores);
      updateMarkers(filteredStores); // keep all filtered stores on map
      renderPagination(totalPages);

      const keyword  = addrInput.value.toLowerCase().trim();
      const selState = stateSelect.value;
      const selCity  = citySelect.value;

      /* Fit map on first page load: if search/filters active fit bounds, else keep closer initial zoom (level 6) */
      if (page === 1 && !userLatLng && filteredStores.length) {
        const validCoords = filteredStores.filter(s => s.latitude != null && s.longitude != null && !isNaN(s.latitude) && !isNaN(s.longitude));
        if ((keyword || selState || selCity) && validCoords.length) {
          const bounds = L.latLngBounds(validCoords.map(s => [s.latitude, s.longitude]));
          map.fitBounds(bounds, { padding: [40, 40] });
        } else {
          map.setView([23.2, 78.5], 6);
        }
      }

      /* Scroll store list back to top on page change */
      storeList.scrollTop = 0;
    }

    /* ── Build pagination controls ── */
    function renderPagination(totalPages) {
      paginationEl.innerHTML = '';

      if (totalPages <= 1) {
        paginationEl.style.display = 'none';
        return;
      }

      paginationEl.style.display = 'flex';

      const makeBtn = (label, page, disabled, active) => {
        const btn = document.createElement('button');
        btn.className = 'sl-page-btn' + (active ? ' sl-page-btn--active' : '');
        btn.textContent = label;
        btn.disabled = disabled;
        if (!disabled) btn.addEventListener('click', () => renderPage(page));
        return btn;
      };

      /* << First */
      paginationEl.appendChild(makeBtn('\u00AB', 1, currentPage === 1, false));
      /* < Prev */
      paginationEl.appendChild(makeBtn('\u2039', currentPage - 1, currentPage === 1, false));

      /* Numbered pages — show at most 5 around current */
      const range = 2;
      const startP = Math.max(1, currentPage - range);
      const endP   = Math.min(totalPages, currentPage + range);

      if (startP > 1) {
        paginationEl.appendChild(makeBtn('1', 1, false, false));
        if (startP > 2) {
          const dots = document.createElement('span');
          dots.className = 'sl-page-dots';
          dots.textContent = '…';
          paginationEl.appendChild(dots);
        }
      }

      for (let p = startP; p <= endP; p++) {
        paginationEl.appendChild(makeBtn(String(p), p, false, p === currentPage));
      }

      if (endP < totalPages) {
        if (endP < totalPages - 1) {
          const dots = document.createElement('span');
          dots.className = 'sl-page-dots';
          dots.textContent = '…';
          paginationEl.appendChild(dots);
        }
        paginationEl.appendChild(makeBtn(String(totalPages), totalPages, false, false));
      }

      /* > Next */
      paginationEl.appendChild(makeBtn('\u203A', currentPage + 1, currentPage === totalPages, false));
      /* >> Last */
      paginationEl.appendChild(makeBtn('\u00BB', totalPages, currentPage === totalPages, false));
    }

    /* ── Results bar ── */
    function showResultsBar(count) {
      countEl.textContent = count;
      resultsBar.style.display = 'flex';
    }

    /* ── Render store cards ── */
    function renderCards(stores) {
      storeList.innerHTML = '';

      if (!stores.length) {
        storeList.innerHTML = `
          <div class="sl-empty-state">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="1.5">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
            </svg>
            <p>No stores found. Try a different search.</p>
          </div>`;
        return;
      }

      stores.forEach(store => {
        const open    = isOpenNow();
        const distStr = store._dist != null ? (store._dist < 1
          ? (store._dist * 1000).toFixed(0) + ' m'
          : store._dist.toFixed(1) + ' km') : '';
        const hours   = window.STORE_HOURS || '10:00 AM – 9:00 PM';
        const days    = window.STORE_DAYS  || 'Mon – Sun';

        const hasCoords = store.latitude != null && store.longitude != null && !isNaN(store.latitude) && !isNaN(store.longitude);
        const navDestination = hasCoords
          ? `${store.latitude},${store.longitude}`
          : encodeURIComponent(`${store.address}, ${store.city}`);

        const card = document.createElement('div');
        card.className = 'sl-store-card';
        card.dataset.id = store.id;

        card.innerHTML = `
          <div class="sl-store-info">
            <div class="sl-store-name">${store.name}</div>
            <div class="sl-store-status ${open ? 'open' : 'closed'}">
              ${open
                ? '&#9679; Open Now &nbsp;&bull; Closes ' + hours.split('\u2013')[1].trim()
                : '&#9679; Closed &nbsp;&bull; Opens ' + hours.split('\u2013')[0].trim()}
            </div>
            <div class="sl-store-address">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              <div class="sl-store-address-lines">
                ${store.address1 ? `<div class="sl-store-address1">${store.address1}</div>` : ''}
                <div class="sl-store-address2">${store.address2 || `${store.city}${store.state ? ', ' + store.state : ''}${store.pincode ? ' - ' + store.pincode : ''}`}</div>
              </div>
            </div>
            <div class="sl-store-phone">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.72 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.63 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              ${store.phone}
              ${distStr ? `<span class="sl-store-dist">&bull; ${distStr}</span>` : ''}
            </div>
            <div class="sl-store-hours">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              ${days} &nbsp;&bull;&nbsp; ${hours}
            </div>
            <div class="sl-card-actions">
              <a class="sl-card-btn"
                 href="https://www.google.com/maps/dir/?api=1&destination=${navDestination}"
                 target="_blank" rel="noopener">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                Directions
              </a>
              <a class="sl-card-btn" href="tel:${store.phone}">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.72 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.63 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                Call
              </a>
            </div>
          </div>`;

        /* Card click → fly to marker */
        card.addEventListener('click', e => {
          if (e.target.closest('a')) return;
          focusStore(store);
        });

        storeList.appendChild(card);
      });
    }

    /* ── Add / update markers ── */
    function updateMarkers(visible) {
      const visibleIds = new Set(visible.map(s => s.id));

      /* Hide all */
      Object.values(markerMap).forEach(({ marker }) => marker.setOpacity(0));

      visible.forEach(store => {
        if (store.latitude == null || store.longitude == null || isNaN(store.latitude) || isNaN(store.longitude)) {
          return;
        }

        if (markerMap[store.id]) {
          markerMap[store.id].marker.setOpacity(1);
        } else {
          const marker = L.marker([store.latitude, store.longitude], {
            icon: redMarkerIcon(false)
          }).addTo(map);

          marker.bindPopup(buildPopup(store), { maxWidth: 240 });

          marker.on('click', () => highlightCard(store.id));
          marker.on('popupopen', () => highlightCard(store.id));

          markerMap[store.id] = { marker, store };
        }
      });
    }

    /* ── Focus: fly map + highlight card ── */
    function focusStore(store) {
      if (store.latitude != null && store.longitude != null && !isNaN(store.latitude) && !isNaN(store.longitude)) {
        map.flyTo([store.latitude, store.longitude], 15, { duration: 0.8 });
        if (markerMap[store.id]) {
          markerMap[store.id].marker.openPopup();
        }
      }
      highlightCard(store.id);
    }

    function highlightCard(id) {
      document.querySelectorAll('.sl-store-card').forEach(c => c.classList.remove('active'));
      const card = document.querySelector(`.sl-store-card[data-id="${id}"]`);
      if (card) {
        card.classList.add('active');
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    /* ── Popup HTML ── */
    function buildPopup(store) {
      const open = isOpenNow();
      const hasCoords = store.latitude != null && store.longitude != null && !isNaN(store.latitude) && !isNaN(store.longitude);
      const navDestination = hasCoords
        ? `${store.latitude},${store.longitude}`
        : encodeURIComponent(`${store.address ? store.address + ', ' : ''}${store.city}`);

      return `
        <div class="sl-popup">
          <h3>${store.name}</h3>
          ${store.address1 ? `<p class="sl-popup-address1">${store.address1}</p>` : ''}
          <p class="sl-popup-address2">${store.address2 || `${store.city}${store.state ? ', ' + store.state : ''}`}</p>
          <p style="color:${open ? '#2e7d32' : '#c8102e'};font-weight:600;font-size:11px;">
            ${open ? '● Open Now' : '● Closed'}
          </p>
          <p>${store.phone}</p>
          <div class="sl-popup-actions">
            <a class="sl-popup-btn sl-popup-btn--dir"
               href="https://www.google.com/maps/dir/?api=1&destination=${navDestination}"
               target="_blank" rel="noopener">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              Directions
            </a>
            <a class="sl-popup-btn sl-popup-btn--call" href="tel:${store.phone}">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.72 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.63 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              Call
            </a>
          </div>
        </div>`;
    }

  });
})();